import { useState, useEffect } from 'react';
import { Box, Typography, useTheme, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import { getUnits, formatPace, formatElevation, formatDistance, formatVerticalSpeed } from '../../utils/units';

// Category → difficulty colour (yellow → orange → red, hardest = darkest red)
const CATEGORY_COLORS = {
  'R-C4': '#ffe066',  // light yellow
  'R-C3': '#ffa020',  // amber / orange
  'R-C2': '#e05000',  // dark orange
  'R-C1': '#c01010',  // red
  'R-HC': '#7a0000',  // deep red
};

const formatGradient = (pct) => (pct != null ? `${pct.toFixed(1)}%` : '-');

const formatDuration = (secs) => {
  if (secs == null) return '-';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

const RunningClimbsGrid = ({ climbs = [], title = 'Detected Climbs', onClimbClick, onClimbDoubleClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [units, setUnits] = useState(getUnits());
  const [zoomedRowId, setZoomedRowId] = useState(null);

  useEffect(() => {
    const handler = (e) => setUnits(e.detail || getUnits());
    window.addEventListener('unitsChanged', handler);
    return () => window.removeEventListener('unitsChanged', handler);
  }, []);

  if (!climbs || climbs.length === 0) {
    return (
      <Box sx={{ p: 3, backgroundColor: colors.grey[800], borderRadius: '8px', textAlign: 'center' }}>
        <Typography color={colors.grey[400]}>No climbs detected in this activity.</Typography>
      </Box>
    );
  }

  const rows = climbs.map((climb, idx) => ({
    id: idx,
    category:         climb.category,
    start_km:         climb.start_distance_m / 1000,
    end_km:           climb.end_distance_m / 1000,
    length_km:        climb.length_m / 1000,
    elevation_gain_m: climb.elevation_gain_m,
    duration_s:       climb.duration_s,
    stopped_time_s:   climb.stopped_time_s,
    avg_gradient_pct: climb.avg_gradient_pct,
    max_gradient_pct: climb.max_gradient_pct,
    avg_pace:         climb.avg_pace,
    avg_gap:          climb.avg_gap,
    avg_heart_rate:   climb.avg_heart_rate,
    avg_vertical_speed: climb.moving_asc_vertical_speed || climb.moving_vertical_speed,
    moving_pace:      climb.moving_pace,
    moving_gap:       climb.moving_gap,
  }));

  const hasHR      = climbs.some(c => c.avg_heart_rate != null);
  const hasMoving  = climbs.some(c => c.moving_pace != null);

  const columns = [
    {
      field: 'category',
      headerName: 'Cat.',
      width: 80,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: CATEGORY_COLORS[params.value] || colors.blueAccent[500],
            color: ['R-C4'].includes(params.value) ? '#3a2800' : '#fff',
            fontWeight: 'bold',
            fontSize: '0.7rem',
          }}
        />
      ),
    },
    {
      field: 'start_km',
      headerName: units === 'imperial' ? 'Start (mi)' : 'Start (km)',
      width: 100,
      valueFormatter: (value) => {
        const v = units === 'imperial' ? value * 0.621371 : value;
        return v.toFixed(2);
      },
    },
    {
      field: 'end_km',
      headerName: units === 'imperial' ? 'End (mi)' : 'End (km)',
      width: 100,
      valueFormatter: (value) => {
        const v = units === 'imperial' ? value * 0.621371 : value;
        return v.toFixed(2);
      },
    },
    {
      field: 'length_km',
      headerName: units === 'imperial' ? 'Length (mi)' : 'Length (km)',
      width: 110,
      valueFormatter: (value) => {
        const v = units === 'imperial' ? value * 0.621371 : value;
        return v.toFixed(2);
      },
    },
    {
      field: 'elevation_gain_m',
      headerName: '↑ Gain',
      width: 90,
      valueFormatter: (value) => formatElevation(value, units),
    },
    {
      field: 'duration_s',
      headerName: 'Duration',
      width: 95,
      valueFormatter: (value) => formatDuration(value),
    },
    {
      field: 'stopped_time_s',
      headerName: 'Stopped',
      width: 90,
      valueFormatter: (value) => formatDuration(value),
    },
    {
      field: 'avg_vertical_speed',
      headerName: 'Moving Asc V.Spd',
      width: 105,
      valueFormatter: (value) => (value != null ? formatVerticalSpeed(value, units) : '-'),
    },
    {
      field: 'avg_gradient_pct',
      headerName: 'Avg %',
      width: 80,
      valueFormatter: (value) => formatGradient(value),
    },
    {
      field: 'max_gradient_pct',
      headerName: 'Max %',
      width: 80,
      valueFormatter: (value) => formatGradient(value),
    },
    {
      field: 'avg_pace',
      headerName: 'Avg Pace',
      width: 100,
      valueFormatter: (value) => (value != null ? formatPace(value, units) : '-'),
    },
    {
      field: 'avg_gap',
      headerName: 'Avg Norm. Pace',
      width: 120,
      valueFormatter: (value) => (value != null ? formatPace(value, units) : '-'),
    },
    ...(hasHR ? [{
      field: 'avg_heart_rate',
      headerName: 'Avg HR',
      width: 90,
      valueFormatter: (value) => (value != null ? `${Math.round(value)} bpm` : '-'),
    }] : []),
    ...(hasMoving ? [
      {
        field: 'moving_pace',
        headerName: 'Mov. Pace',
        width: 105,
        valueFormatter: (value) => (value != null ? formatPace(value, units) : '-'),
      },
      {
        field: 'moving_gap',
        headerName: 'Mov. Norm. Pace',
        width: 105,
        valueFormatter: (value) => (value != null ? formatPace(value, units) : '-'),
      },
    ] : []),
  ];

  const gridHeight = Math.min(108 + rows.length * 52, 600);

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb={2}>
        {title}
      </Typography>
      <Typography variant="caption" color={colors.grey[400]} mb={1} display="block">
        Click to highlight in chart. Double-click to zoom chart and map to that section.
      </Typography>
      <Box
        sx={{
          height: gridHeight,
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: `1px solid ${colors.grey[700]}`, color: colors.grey[100] },
          '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.grey[700], color: colors.grey[100], borderBottom: 'none' },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.grey[800] },
          '& .MuiDataGrid-footerContainer': { backgroundColor: colors.grey[700], color: colors.grey[100], borderTop: 'none' },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': { color: colors.grey[100] },
          '& .MuiCheckbox-root': { color: `${colors.blueAccent[400]} !important` },
          '& .MuiDataGrid-row.row-zoomed': { backgroundColor: `${colors.grey[600]} !important` },
          '& .MuiDataGrid-row:hover': { backgroundColor: `${colors.grey[700]} !important` },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          density="compact"
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          getRowClassName={(params) => params.row.id === zoomedRowId ? 'row-zoomed' : ''}
          onRowClick={(params) => {
            const climb = climbs[params.row.id];
            const payload = {
              leftKm:  climb.start_distance_m / 1000,
              rightKm: climb.end_distance_m / 1000,
              color:   CATEGORY_COLORS[climb.category] || '#e05000',
            };
            if (onClimbClick) onClimbClick(payload);
          }}
          onRowDoubleClick={onClimbDoubleClick ? (params) => {
            const climb = climbs[params.row.id];
            setZoomedRowId(params.row.id);
            onClimbDoubleClick({
              leftKm:  climb.start_distance_m / 1000,
              rightKm: climb.end_distance_m / 1000,
              color:   CATEGORY_COLORS[climb.category] || '#e05000',
            });
          } : undefined}
          sx={{ cursor: 'pointer' }}
        />
      </Box>
    </Box>
  );
};

export default RunningClimbsGrid;
